import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.tsx';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { Button, Checkbox, Drawer, Flex, Text } from '@mantine/core';
import React, { useEffect, useMemo } from 'react';
import { FormProvider } from 'react-hook-form';
import { Select } from 'react-hook-form-mantine';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling.tsx';
import { INVESTMENT_PROGRAMME_FORM_FIELDS } from './utils/types.ts';
import { useLinkInvestmentProgrammeDrawer } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/InvestmentProgrammeDrawer/hooks/useLinkInvestmentProgrammeDrawer.ts';
import { constructTransactionSentence } from '@dealstack/frontend/shared/utils/constructTransactionSentence.ts';
import { InfoBox } from '@dealstack/frontend/shared/components/InfoBox/InfoBox.tsx';
import { useQuery } from '@tanstack/react-query';
import { getAvailableMipsForPerson } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/InvestmentProgrammeDrawer/apis/useInvestmentProgramme.ts';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { makeOptionWithTestId } from '@dealstack/frontend/shared/components/Select/withOptionTestId';

const MipOption = makeOptionWithTestId(
	TEST_ELEMENT_IDS.CAP_INVESTMENT_PROGRAMMES_LINK_DRAWER_MIP_OPTION
);

interface Props {
	hasPermission: boolean;
	opened: boolean;
	onClose: () => void;
}

export const LinkInvestmentProgrammeDrawer = ({ hasPermission, opened, onClose }: Props) => {
	const tCapManage = useTranslationCapManage();

	const {
		linkMipForm,
		onFormSubmit,
		isLoading,
		personTransactionForMip,
		linkTransactions,
		setLinkTransactions,
		selectedEntityId,
	} = useLinkInvestmentProgrammeDrawer(onClose);

	const availableMipRequest = useQuery({
		queryKey: ['availableMipsToLink', selectedEntityId],
		queryFn: () => {
			if (!selectedEntityId) {
				return Promise.resolve([]);
			}

			return getAvailableMipsForPerson({
				personId: selectedEntityId,
			});
		},
		enabled: false,
	});
	const availableMips = useMemo(() => availableMipRequest.data ?? [], [availableMipRequest.data]);

	const availableMipOptions = availableMips.map((p) => ({
		label: p.programmeName ?? '',
		value: p.id,
	}));

	const hasNoAvailableMips = !availableMips?.length && !availableMipRequest.isFetching;

	useEffect(() => {
		if (!opened) {
			linkMipForm.reset({});
		} else {
			void availableMipRequest.refetch();
		}
	}, [opened]);

	return (
		<Drawer
			opened={opened}
			onClose={onClose}
			position="right"
			transitionProps={drawerTransitionProps}
			title={tCapManage('person_investment_programme.drawer.add_title')}
		>
			<FormProvider {...linkMipForm}>
				<Flex direction="column" justify="space-between" h="100%">
					<Flex direction="column" gap="1rem">
						<Select
							name={INVESTMENT_PROGRAMME_FORM_FIELDS.mip_id}
							control={linkMipForm.control}
							label={tCapManage('person_investment_programme.drawer.form.mip_label')}
							placeholder={tCapManage(
								'person_investment_programme.drawer.form.mip_placeholder'
							)}
							data={availableMipOptions}
							rightSection={<Icon variant="caret-down" size="lg" />}
							styles={{ rightSection: { pointerEvents: 'none' } }}
							searchable
							size="md"
							disabled={!hasPermission || hasNoAvailableMips}
							data-testid={
								TEST_ELEMENT_IDS.CAP_INVESTMENT_PROGRAMMES_LINK_DRAWER_MIP_SELECT
							}
							renderOption={MipOption}
						/>

						{hasNoAvailableMips && (
							<InfoBox
								iconVariant="information"
								alignContent="center"
								description={tCapManage(
									'person_investment_programme.drawer.no_mip_warning'
								)}
							></InfoBox>
						)}

						{!!personTransactionForMip?.length && (
							<Flex direction="column" gap="0.25rem">
								<Checkbox
									checked={linkTransactions}
									onChange={() => setLinkTransactions(!linkTransactions)}
									label={tCapManage(
										'person_investment_programme.drawer.form.link_transactions_label',
										{
											count: personTransactionForMip.length,
										}
									)}
									data-testid={
										TEST_ELEMENT_IDS.CAP_INVESTMENT_PROGRAMMES_LINK_DRAWER_TRANSACTIONS_CHECKBOX
									}
								/>
								{personTransactionForMip.map((t) => (
									<Text key={t.id}>
										{constructTransactionSentence({
											transactionType: t.type,
											units: t.units ?? 0,
											securityUnitDenominator: t.securityUnitDenominator,
											transactionDate: t.date,
											buyerName: t.destinationPersonName,
											sellerName: t.sourcePersonName,
											securityName: t.securityName ?? '',
											cost: t.cost ?? 0,
										})}
									</Text>
								))}
							</Flex>
						)}
					</Flex>

					<Button
						onClick={linkMipForm.handleSubmit(onFormSubmit)}
						disabled={isLoading || hasNoAvailableMips}
						data-testid={
							TEST_ELEMENT_IDS.CAP_INVESTMENT_PROGRAMMES_LINK_DRAWER_SUBMIT_BUTTON
						}
					>
						{tCapManage('person_investment_programme.drawer.add_button')}
					</Button>
				</Flex>
			</FormProvider>
		</Drawer>
	);
};
